import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useState, Suspense, lazy } from 'react';
import CookieConsent, { getCookieConsentValue } from 'react-cookie-consent';
import ProtectedRoute from './ProtectedRoute';
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, analytics } from "./firebase";
import { initAnalytics, trackPageView } from './utils/analytics';

// Lazy load components for code splitting
// Keep HomePage eager-loaded for better initial load experience
import HomePage from './HomePage';
const ResumeForm = lazy(() => import('./ResumeForm'));
const Login = lazy(() => import('./Login'));
const Dashboard = lazy(() => import('./Dashboard'));
const SmartStartWizard = lazy(() => import('./components/SmartStartWizard'));
const Terms = lazy(() => import('./components/Terms'));
const Privacy = lazy(() => import('./components/Privacy'));
const TemplateSelector = lazy(() => import('./TemplateSelector'));
const ResetPassword = lazy(() => import('./ResetPassword'));
const FeedbackDashboard = lazy(() => import('./components/FeedbackDashboard'));
const ProfileSettings = lazy(() => import('./ProfileSettings'));
const SuccessPage = lazy(() => import('./SuccessPage'));
const CancelPage = lazy(() => import('./CancelPage'));
const PricingPage = lazy(() => import('./PricingPage'));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Initialize Analytics on app load — but only if the user has accepted cookies.
// (GDPR / ePrivacy: don't load analytics before consent.)
if (analytics && getCookieConsentValue() === 'true') {
  initAnalytics(analytics);
}

// Component to track page views on route changes
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function App() {
  const [user] = useAuthState(auth);
  // Admin status is read from the Firebase ID token's custom claim `admin: true`.
  // Set with `admin.auth().setCustomUserClaims(uid, { admin: true })` from a
  // privileged tool — never trust a client-side email match.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!user) { setIsAdmin(false); return; }
    user.getIdTokenResult()
      .then((res) => { if (!cancelled) setIsAdmin(res.claims?.admin === true); })
      .catch(() => { if (!cancelled) setIsAdmin(false); });
    return () => { cancelled = true; };
  }, [user]);

  return (
    <Router>
      <PageViewTracker />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route 
          path="/builder" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <ResumeForm />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <Dashboard />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/login" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Login />
            </Suspense>
          } 
        />
        <Route 
          path="/smart-start" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <SmartStartWizard />
            </Suspense>
          } 
        />
        <Route 
          path="/terms" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Terms />
            </Suspense>
          } 
        />
        <Route 
          path="/privacy" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Privacy />
            </Suspense>
          } 
        />
        <Route 
          path="/pricing" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <PricingPage />
            </Suspense>
          } 
        />
        <Route 
          path="/select-template" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <TemplateSelector />
            </Suspense>
          } 
        />
        <Route 
          path="/reset-password" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ResetPassword />
            </Suspense>
          } 
        />
        {isAdmin && (
          <Route
            path="/admin/feedback"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <FeedbackDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
        )}
        <Route 
          path="/profile-settings" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <ProfileSettings />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/success" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <SuccessPage />
            </Suspense>
          } 
        />
        <Route 
          path="/cancel" 
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <CancelPage />
            </Suspense>
          } 
        />
      </Routes>
      <CookieConsent
        location="bottom"
        buttonText="Accept"
        declineButtonText="Reject"
        enableDeclineButton
        cookieName="cwr-cookie-consent"
        style={{ background: "#2B373B" }}
        buttonStyle={{ color: "#fff", background: "#007bff", borderRadius: "5px", padding: "6px 16px" }}
        declineButtonStyle={{ color: "#fff", background: "#6b7280", borderRadius: "5px", padding: "6px 16px" }}
        expires={150}
        onAccept={() => {
          if (analytics) initAnalytics(analytics);
        }}
      >
        We use cookies to enhance your experience and analyze traffic. You can accept or reject non-essential cookies.
        <a href="/privacy" className="underline text-blue-300 ml-1">Learn more</a>
      </CookieConsent>
    </Router>
  );
}

export default App;